'use client';

import classes from "./public_filter.module.css";
import ChevronDown from '@/assets/svg/icons/chevron-down.svg';
import ChevronUp from '@/assets/svg/icons/chevron-up.svg';
import CustomSVG from "../common/svg";
import { forwardRef, useContext, useEffect, useRef, useState } from "react";
import FilterContext from "@/providers/filter-context";
import FormControl from "../common/checks_and_radios/form_control";
import TextInput from "../common/inputs/text_input";
import Search from '@/assets/svg/icons/search.svg';
import { Dropdown } from "react-bootstrap";
import Check from '@/assets/svg/icons/check.svg';
import X from '@/assets/svg/icons/x.svg';
import Filter from '@/assets/svg/icons/filter.svg';
import InputSlider from "../common/input_slider/input_slider";
import ProviderInfoContext from "@/providers/provider-info-context";
import { useParams, usePathname, useRouter, useSearchParams } from "next/navigation";
import { normalizeString } from "@/functions/normalize_string";
import { normalizeSearchText } from "@/functions/normalize_search_text";
import CustomBadge from "../common/badges/badge";
import ProviderDropdownPublic from "../common/provider_selector_simple/provider_selector_public";
// useLoader import eltávolítva - nem használjuk

const compareCityNames = (a = "", b = "") => a.localeCompare(b, 'hu', { sensitivity: 'base', numeric: true });
const isInputTarget = (target) => target instanceof Element && target.closest("input");

const CustomToggle = forwardRef(function CustomToggle({ children, onClick, value, setValue, active, onFocus, onBlur }, ref) {
    return (
        <div
            ref={ref}
            className="d-flex flex-column"
            style={{gap: 6}}
            onMouseDown={(e) => {
                if (!isInputTarget(e.target)) {
                    e.preventDefault();
                }
            }}
            onClick={(e) => {
                if (isInputTarget(e.target)) {
                    return;
                }

                e.preventDefault();
                onClick?.(e);
            }}
        >
            <TextInput
                className={`w-100`}
                value={value}
                onChange={e => setValue(e.target.value)}
                onFocus={(e) => {
                    onFocus?.(e);
                    if (!active) onClick?.(e);
                }}
                onBlur={onBlur}
                type={"text"}
                label={"Hol keresel?"}
                placeholder={"Város kiválasztása"}
                help_icon={active ? ChevronUp : ChevronDown}
            />
        </div>
    )
});

export default function PublicFilter({}) {
    const filters = useContext(FilterContext);
    const providerInfos = useContext(ProviderInfoContext);
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const use_params = useParams();
    // Loader context eltávolítva - nem használjuk

    const [mainIsOpen, setMainIsOpen] = useState(true);
    const [cityPickerOpen, setCityPickerOpen] = useState(false);
    const [cityName, setCityName] = useState(filters.addressCity);
    const [mobileFilter, setMobileFilter] = useState(false);
    const [trueSearch, setTrueSearch] = useState("");
    const hasMountedNameEffectRef = useRef(false);

    const cities = providerInfos.cities;

    const toggleMainIsOpen = () => { setMainIsOpen(!mainIsOpen); };
    const handleCityPickerToggle = (nextShow) => {
        setCityPickerOpen(nextShow);
        if (nextShow) {
            setTrueSearch(cityName || "");
        }
    };
    const toggleMobileFilter = () => { setMobileFilter(!mobileFilter); };

    // const handleFavoritToggle = (val) => {
    //     filters.setIsFavorit(val);
    //     const params = new URLSearchParams(Array.from(searchParams.entries()));
    //     params.delete("page");
    //     if(val) {
    //         params.set("is_favourite", 1);
    //     } else {
    //         params.delete("is_favourite");
    //     }
    //     router.push(`${pathname}?${decodeURIComponent(params.toString())}`, {scroll: false});
    // }

    // const handleSearchChange = (val) => {
    //     filters.setName(val);
    // }

    useEffect(() => {
        if (!hasMountedNameEffectRef.current) {
            hasMountedNameEffectRef.current = true;
            return;
        }

        const currentNameParam = searchParams.get("name");
        const normalizedFilterName = filters.name.trim() ? `*${filters.name}*` : null;

        if (currentNameParam === normalizedFilterName || (!currentNameParam && !normalizedFilterName)) {
            return;
        }

        const delayDebounceFn = setTimeout(() => {
            const params = new URLSearchParams(Array.from(searchParams.entries()));
            params.delete("page");
            if(filters.name.trim()) {
                params.set("name", `*${filters.name}*`);
            } else {
                params.delete("name");
            }
            router.push(`${pathname}?${decodeURIComponent(params.toString())}`, {scroll: false});
        }, 1000);
      
        return () => clearTimeout(delayDebounceFn)
    }, [filters.name, pathname, router, searchParams])

    const getSelectedCityUrl = (selectedCity) => {
        const normalizedCityName = normalizeString(selectedCity);
        const params = new URLSearchParams(Array.from(searchParams.entries()));
        params.delete("page");

        const queryString = decodeURIComponent(params.toString());
        return queryString
            ? `/${use_params["provider_type"]}/${normalizedCityName}?${queryString}`
            : `/${use_params["provider_type"]}/${normalizedCityName}`;
    }

    const selectCity = (val) => {
        setCityPickerOpen(false);
        setCityName(val);
        setTrueSearch(val);
        filters.setAddressCity(val);

        const normalizedCityName = normalizeString(val);
        if (use_params["address_city"] !== normalizedCityName) {
            router.push(getSelectedCityUrl(val), {scroll: false});
        }
    }

    const selectHandler = (e, val) => {
        e.preventDefault();
        e.stopPropagation();
        selectCity(val);
    }

    const handleDistanceChange = (val) => {
        // Loader eltávolítva - router navigation már jelzi a változást
        filters.setDistance(val);
        const params = new URLSearchParams(Array.from(searchParams.entries()));
        params.delete("page");
        if(+val) {
            params.set("distance", val);
        } else {
            params.delete("distance");
        }
        router.push(`${pathname}?${decodeURIComponent(params.toString())}`, {scroll: false});
    }

    const handleCityChange = (val) => {
        // Csak frissítjük a state-et, nincs navigálás
        setCityName(val);
        setTrueSearch(val);
        if (!cityPickerOpen) setCityPickerOpen(true);
    }

    const handleCityFocus = () => {
        if (!cityPickerOpen) setCityPickerOpen(true);
        setTrueSearch(cityName || "");
    }

    const handleCityBlur = () => {
        setTimeout(() => {
            setCityPickerOpen(false);
            const trimmed = (cityName || "").trim();
            if (!trimmed) {
                setCityName(filters.addressCity || "");
                setTrueSearch(filters.addressCity || "");
                return;
            }

            const matchedCity = cities.find(
                c => c?.city && normalizeSearchText(c.city) === normalizeSearchText(trimmed)
            )?.city;
            if (matchedCity) {
                selectCity(matchedCity);
                return;
            }

            setCityName(filters.addressCity || "");
            setTrueSearch(filters.addressCity || "");
        }, 150);
    }

    useEffect(() => {
        if (cityPickerOpen) {
            return;
        }

        const selectedCity = filters.addressCity || "";
        if (cityName !== selectedCity) {
            setCityName(selectedCity);
        }
        if (trueSearch !== selectedCity) {
            setTrueSearch(selectedCity);
        }
    }, [cityName, cityPickerOpen, filters.addressCity, trueSearch])

    const cityTypePriority = {
        region: 0,
        county: 1,
        district: 3,
        city: 3
    };

    let filteredCities = cities
        .filter(c => {
            // Defensive check: csak akkor szűrünk, ha minden szükséges adat megvan
            if (!c || typeof c.city !== 'string' || typeof trueSearch !== 'string') {
                return false;
            }
            return normalizeSearchText(c.city).includes(normalizeSearchText(trueSearch));
        })
        .sort((a, b) => {
            const typeA = cityTypePriority[a?.type] ?? cityTypePriority.city;
            const typeB = cityTypePriority[b?.type] ?? cityTypePriority.city;
            if (typeA !== typeB) return typeA - typeB;
            return compareCityNames(a?.city, b?.city);
        });

    let badge_cnt = filters.getFilterBadges().length;
    if(filters.addressCity) badge_cnt++;

    const onProviderSelect = (provider) => {
        if(provider.url) router.push(`/${provider.url}`)
    }

    return <>
        <div className={`${classes["public-filter-mobile-search-container"]}`}>
            {/*<div className="flex-grow-1">
                <TextInput className="w-100" type="text" label={"Keresés a szolgáltatók között"} placeholder="Keresés" leading_icon={Search} value={filters.name} onChange={e => handleSearchChange(e.target.value)} />
            </div>*/}
            <ProviderDropdownPublic starting_values={{favourites: [], default_providers: []}} providerId={0} setProviderId={() => {}} starting_name={""} has_switch={false} title={"Keresés a szolgáltatók között"} onSelect={onProviderSelect} placeholder={"Keresés"} searchIn={use_params["provider_type"]} />
            <div className="position-relative flex-shrink-0 padding-3">
                <CustomSVG className="flex-shrink-0 stroke-gray-800 pointer" SvgFunction={Filter} height={20} width={20} onClick={toggleMobileFilter} />
                {badge_cnt > 0 && <div className={`${classes["filter-count-badge"]} color-gray-800 text-xs-regular`}>
                    {badge_cnt}
                </div>}
            </div>
        </div>
        <div className={`${classes["public-filter-main-container"]} ${mobileFilter ? classes["mobile-active"] : ""}`}>
            <div className={`${classes["public-filter-main-header"]}`}>
                <p className="margin-0 color-gray-900 text-md-medium flex-grow-1 text-truncate">Szűrés</p>
                <CustomSVG className="flex-shrink-0 stroke-gray-900 pointer" SvgFunction={X} height={16} width={16} onClick={() => setMobileFilter(false)} />
            </div>
            <div className={`${classes["public-filter-main-container-inner"]}`} >
                <ActiveFilters />
                <div className="d-flex flex-row align-items-center align-self-stretch justify-content-between">
                    <p className="margin-0 text-xs-regular color-gray-500 text-uppercase">Keresés</p>
                    {/*<CustomSVG className="flex-shrink-0 stroke-gray-800 pointer" SvgFunction={mainIsOpen ? ChevronUp : ChevronDown} height={20} width={20} onClick={toggleMainIsOpen} />*/}
                </div>
                {/*mainIsOpen && */<div className={`${classes["filter-section-container"]}`} >
                    {/* <FormControl id={`public-filter-is-favorit`} type="switch" size={"md"} main_text={"Csak a kedvenc szolgáltatók"} checked={filters.isFavorit} onChange={() => handleFavoritToggle(!filters.isFavorit)} /> */}
                    {/*<TextInput className="w-100" type="text" label={"Keresés a szolgáltatók között"} placeholder="Keresés" leading_icon={Search} value={filters.name} onChange={e => handleSearchChange(e.target.value)} />*/}
                    <ProviderDropdownPublic starting_values={{favourites: [], default_providers: []}} providerId={0} setProviderId={() => {}} starting_name={""} has_switch={false} title={"Keresés a szolgáltatók között"} onSelect={onProviderSelect} placeholder={"Keresés"} searchIn={use_params["provider_type"]} />
                    <div className="d-flex flex-column align-self-stretch">
                        <p className="margin-0 text-xs-regular color-gray-500 text-uppercase">Részletes</p>
                        <div className={`${classes["filter-section-container"]}`} >
                            <Dropdown show={cityPickerOpen} onToggle={handleCityPickerToggle} className={`w-100`} >
                                <Dropdown.Toggle
                                    as={CustomToggle}
                                    active={cityPickerOpen}
                                    value={cityName}
                                    setValue={handleCityChange}
                                    onFocus={handleCityFocus}
                                    onBlur={handleCityBlur}
                                />
                                <Dropdown.Menu className="w-100 overflow-y-auto" style={{maxHeight: 300}}>
                                    {filteredCities && filteredCities.length > 0 && filteredCities.map(c => {
                                        const isPromoted = c?.type === "region" || c?.type === "county";
                                        return (
                                            <Dropdown.Item
                                                key={`public-filter-city-${c.city}`}
                                                className={`d-flex justify-content-between ${classes["dropdown-city-item"]} ${isPromoted ? classes["dropdown-city-item-promoted"] : ""}`}
                                                onMouseDown={(e) => selectHandler(e, c.city)}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                }}
                                                active={c.city === filters.addressCity}
                                            >
                                        {c.city}
                                        {c.city === filters.addressCity && <CustomSVG className="stroke-gray-900" SvgFunction={Check} height={20} width={20} />}
                                            </Dropdown.Item>
                                        );
                                    })}
                                </Dropdown.Menu>
                            </Dropdown>
                            <div className="d-flex flex-column align-self-stretch" style={{gap: 6}}>
                                <InputSlider label={"Távolság"} min="0" max="100" step="5" value={filters.distance} onMouseUp={e => handleDistanceChange(e.target.value)} onChange={e => filters.setDistance(e.target.value)} />
                                <div className={`${classes["distance-display"]}`}>
                                    <p className="margin-0 color-gray-700 text-md-medium">{`${filters.distance} km`}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>}
                {filters.others && filters.others.length > 0 && filters.others.map(f => <OtherSection key={`filter-section-${f.filter_name}`} filter={f} filters={filters.others} setFilters={filters.setOthers} />)}
            </div>
        </div>
    </>
}

function OtherSection({filter, filters, setFilters}) {
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    // Loader context eltávolítva - nem használjuk

    const [isOpen, setIsOpen] = useState(true);
    const toggleIsOpen = () => { setIsOpen(!isOpen); };

    const handleChange = (e, val, type) => {
        e.preventDefault();
        e.stopPropagation();

        // Loader eltávolítva - router navigation már jelzi a változást

        if(type === "radio") {
            setFilters(prev => {
                let temp_prev = [...prev];
                let temp_index = temp_prev.findIndex(p => p.filter_name === filter.filter_name);
                if(temp_index === -1) return prev;

                temp_prev[temp_index].value = val;
                return temp_prev;
            })

            const params = new URLSearchParams(Array.from(searchParams.entries()));
            params.delete("page");
            if(val) {
                params.set(filter.filter_name, val);
            } else {
                params.delete(filter.filter_name);
            }
            console.debug(params)
            console.debug(params.toString())
            router.push(`${pathname}?${decodeURIComponent(params.toString())}`, {scroll: false});
        }

        if(type === "checkbox") {
            let temp = [...filters];
            let temp_index = temp.findIndex(p => p.filter_name === filter.filter_name);
            if(temp_index === -1) return;

            let temp_values = [];
            if(temp[temp_index].value) temp_values = temp[temp_index].value.split(",");

            let new_value;
            if(temp_values.length === 0) new_value = val;
            else if(temp_values.includes(val)) new_value = temp_values.filter(t => t !== val).join();
            else new_value = [...temp_values, val].join();

            temp[temp_index].value = new_value;
            setFilters(temp);

            const params = new URLSearchParams(Array.from(searchParams.entries()));
            params.delete("page");
            if(new_value) {
                params.set(filter.filter_name, new_value);
            } else {
                params.delete(filter.filter_name);
            }
            router.push(`${pathname}?${decodeURIComponent(params.toString())}`, {scroll: false});
        }
    }

    return <div className="d-flex flex-column align-self-stretch">
        <div className="d-flex flex-row align-items-center align-self-stretch justify-content-between">
            <p className="margin-0 text-xs-regular color-gray-500 text-uppercase">{filter.name}</p>
            {/*<CustomSVG className="flex-shrink-0 stroke-gray-800 pointer" SvgFunction={isOpen ? ChevronUp : ChevronDown} height={20} width={20} onClick={toggleIsOpen} />*/}
        </div>
        {/*isOpen && */<div className={`${classes["filter-section-container"]}`} >
            {filter.type === "radio" && <>
                <div key={`filter-section-radio-${filter.filter_name}-empty`} className={`align-items-center`} onClick={e => {handleChange(e, "", "radio")}}>
                    <FormControl name={`filter-section-radio-${filter.filter_name}-empty`} className="align-items-center" theme="primary" type="radio" size="md" onClick={() => {}} checked={"" === filter.value} main_text={"Nincs kiválasztva"} readOnly/>
                </div>
                {filter.fields.map(f => <div key={`filter-section-radio-${filter.filter_name}-${f.filter_name}`} className={`align-items-center`} onClick={e => {handleChange(e, f.filter_name, "radio")}}>
                    <FormControl name={`filter-section-radio-${filter.filter_name}-${f.filter_name}`} className="align-items-center" theme="primary" type="radio" size="md" onClick={() => {}} checked={f.filter_name === filter.value} main_text={f.name} readOnly/>
                </div>)}
            </>}
            {filter.type === "checkbox" && filter.fields.map(f => <div key={`filter-section-checkbox-${filter.filter_name}-${f.filter_name}-${(filter.value && filter.value.split(",").includes(f.filter_name))}`} onClick={e => handleChange(e, f.filter_name, "checkbox")}>
                <FormControl className={`align-items-center`} type="checkbox" size="md" theme="primary" checked={(filter.value && filter.value.split(",").includes(f.filter_name))} main_text={f.name} readOnly />
            </div>)}
        </div>}
    </div>
}

function ActiveFilters({}) {
    const filters = useContext(FilterContext);
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    
    const [isOpen, setIsOpen] = useState(true);
    const toggleOpen = () => { setIsOpen(!isOpen); };

    /*let temp_search_params = [];
    for (const [key, value] of searchParams.entries()) {
        let affix = "";
        if(key === "distance") affix = " km";

        let temp_value = value; 
        if(key === "is_favourite") temp_value = "kedvenc";
        if(key === "name") temp_value = value.substring(1, value.length-1);

        if(key !== "order" && key !== "per_page") temp_search_params.push({name: key, value: temp_value, affix: affix});
    }*/

    const removeFilter = (key, removeFunction) => {
        let res = removeFunction();
        if(res && res === "delete") {
            updateURL(key);
        } else if(res) {
            const params = new URLSearchParams(Array.from(searchParams.entries()));
            params.delete("page");
            params.set(key, res);
            router.push(`${pathname}?${decodeURIComponent(params.toString())}`, {scroll: false});
        }
    }

    const updateURL = (key) => {
        filters.removeFilter(key);
        const params = new URLSearchParams(Array.from(searchParams.entries()));
        params.delete("page");
        params.delete(key);
        router.push(`${pathname}?${decodeURIComponent(params.toString())}`, {scroll: false});
    }

    return <div className="d-flex flex-column align-self-stretch">
        {(!(!filters.addressCity && filters.getFilterBadges().length === 0)) && <>
            <div className="d-flex flex-row align-items-center align-self-stretch justify-content-between">
                <p className="margin-0 text-xs-regular color-gray-500 text-uppercase">Aktív szűrés</p>
                {/*<CustomSVG className="flex-shrink-0 stroke-gray-800 pointer" SvgFunction={isOpen ? ChevronUp : ChevronDown} height={20} width={20} onClick={toggleOpen} />*/}
            </div>
            {/*isOpen && -*/<div className={`${classes["filter-section-container"]} flex-row flex-wrap`} >
                {filters.addressCity && <CustomBadge size="sm" color={`gray`} >{filters.addressCity}</CustomBadge>}
                {/*temp_search_params.length > 0 && temp_search_params.map(t => <CustomBadge key={`search-params-badge-${t.name}`} size="sm" color={`primary`} >
                    {`${t.value}${t.affix}`}
                    <CustomSVG className="flex-shrink-0 stroke-primary-700 pointer" SvgFunction={X} height={12} width={12} onClick={() => removeFilter(t.name)} />
                </CustomBadge>)*/}
                {filters.getFilterBadges().length > 0 && filters.getFilterBadges().map((f, i) => <CustomBadge key={`search-params-badge-${f.key}-${i}`} size="sm" color={`primary`} >
                    {f.value}
                    <CustomSVG className="flex-shrink-0 stroke-primary-700 pointer" SvgFunction={X} height={12} width={12} onClick={() => removeFilter(f.key, f.removeFunction)} />
                </CustomBadge>)}
            </div>}
        </>}
    </div>
}